import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
} from "@dg/ui";
import { useAuth } from "../contexts/auth.js";
import { useTheme } from "../contexts/theme.js";
import {
  updateProfile,
  updatePreferences,
  getProviders,
  linkProvider,
  unlinkProvider,
  getSessions,
  revokeSession,
  revokeAllOtherSessions,
  getAccountLogs,
  deleteAccount,
  isApiError,
  type LinkedProvider,
  type UserSession,
  type AccountLogEntry,
} from "../lib/api.js";

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function parseUserAgent(ua: string | null): string {
  if (!ua) return "Unknown device";
  if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS device";
  if (ua.includes("Android")) return "Android device";
  if (ua.includes("Mac")) return "Mac";
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Linux")) return "Linux";
  return "Browser";
}

// ── Profile section ────────────────────────────────────────────────────────

function ProfileSection() {
  const { auth, refresh } = useAuth();
  const [displayName, setDisplayName] = useState(
    auth.status === "active" ? (auth.user.displayName ?? "") : "",
  );
  const [saving, setSaving] = useState(false);

  if (auth.status !== "active") return null;
  const { user } = auth;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const trimmed = displayName.trim();
      await updateProfile(trimmed ? { displayName: trimmed } : {});
      toast.success("Profile updated.");
      await refresh();
    } catch {
      toast.error("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Profile</h2>
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={user.avatarUrl ?? undefined} alt="" />
          <AvatarFallback className="text-lg">
            {(user.displayName ?? user.email ?? "?")[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="font-medium">{user.displayName ?? "—"}</div>
          <div className="text-sm text-muted-foreground truncate">
            {user.email ?? "—"}
          </div>
          <Badge variant="outline" className="mt-1 capitalize text-xs">
            {user.primaryProvider ?? "—"}
          </Badge>
        </div>
      </div>

      <form onSubmit={(e) => void handleSave(e)} className="space-y-3 max-w-sm">
        <div className="space-y-1.5">
          <Label htmlFor="display-name">Display name</Label>
          <Input
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={100}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input value={user.email ?? "—"} disabled />
          <p className="text-xs text-muted-foreground">
            Email comes from your connected provider.
          </p>
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </form>
    </section>
  );
}

// ── Preferences section ────────────────────────────────────────────────────

function PreferencesSection() {
  const { setTheme, theme } = useTheme();
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [saving, setSaving] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updatePreferences({ theme, timezone });
      toast.success("Preferences saved.");
    } catch {
      toast.error("Failed to save preferences.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Preferences</h2>
      <form onSubmit={(e) => void handleSave(e)} className="space-y-4 max-w-sm">
        <div className="space-y-1.5">
          <Label>Theme</Label>
          <Select
            value={theme}
            onValueChange={(v) =>
              setTheme(v as "light" | "dark" | "lilac-light" | "lilac-dark")
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="lilac-light">Lilac Light</SelectItem>
              <SelectItem value="lilac-dark">Lilac Dark</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="timezone">Timezone</Label>
          <Input
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="Australia/Sydney"
            maxLength={64}
          />
          <p className="text-xs text-muted-foreground">
            Used to display timestamps across the platform.
          </p>
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save preferences"}
        </Button>
      </form>
    </section>
  );
}

// ── Connected accounts section ─────────────────────────────────────────────

const ALL_PROVIDERS = ["github", "discord", "linkedin"] as const;
type Provider = (typeof ALL_PROVIDERS)[number];

function ConnectedAccountsSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState<LinkedProvider[] | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  async function load() {
    try {
      const { data } = await getProviders();
      setProviders(data);
    } catch {
      toast.error("Failed to load connected accounts.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Show success toast when returning from an OAuth link flow
  useEffect(() => {
    const linked = searchParams.get("linked");
    if (linked) {
      toast.success(
        `${linked.charAt(0).toUpperCase() + linked.slice(1)} connected successfully.`,
      );
      setSearchParams((prev) => {
        prev.delete("linked");
        return prev;
      });
      void load();
    }
  }, [searchParams, setSearchParams]);

  async function handleUnlink(provider: Provider) {
    setActing(provider);
    try {
      await unlinkProvider(provider);
      toast.success(
        `${provider.charAt(0).toUpperCase() + provider.slice(1)} disconnected.`,
      );
      await load();
    } catch (err) {
      const msg =
        isApiError(err) && err.status === 400
          ? ((err.body?.error as { message?: string } | undefined)?.message ??
            "Cannot remove your only linked provider.")
          : "Failed to disconnect provider.";
      toast.error(msg);
    } finally {
      setActing(null);
    }
  }

  async function handleLink(provider: "discord" | "linkedin") {
    setActing(provider);
    try {
      await linkProvider(provider);
      // Page navigates away — no finally cleanup needed
    } catch {
      toast.error("Failed to start connection flow.");
      setActing(null);
    }
  }

  const linkedIds = new Set(providers?.map((p) => p.providerId) ?? []);
  const isOnlyProvider = linkedIds.size <= 1;

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Connected Accounts</h2>
      {providers === null ? (
        <div className="space-y-2 max-w-sm">
          {ALL_PROVIDERS.map((p) => (
            <Skeleton key={p} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="space-y-2 max-w-sm">
          {ALL_PROVIDERS.map((provider) => {
            const linked = providers.find((p) => p.providerId === provider);
            const isLinked = Boolean(linked);
            const isDisabled = acting !== null;
            const label = provider.charAt(0).toUpperCase() + provider.slice(1);

            return (
              <Card key={provider} className="p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{label}</div>
                    {linked?.providerEmail && (
                      <div className="text-xs text-muted-foreground truncate">
                        {linked.providerEmail}
                      </div>
                    )}
                  </div>
                  {isLinked ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isDisabled || isOnlyProvider}
                      title={
                        isOnlyProvider
                          ? "Cannot remove your only linked provider"
                          : undefined
                      }
                      onClick={() => void handleUnlink(provider)}
                    >
                      {acting === provider ? "Disconnecting…" : "Disconnect"}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      disabled={isDisabled || provider === "github"}
                      onClick={() =>
                        void handleLink(provider as "discord" | "linkedin")
                      }
                    >
                      {acting === provider ? "Connecting…" : "Connect"}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ── Sessions section ───────────────────────────────────────────────────────

function SessionsSection() {
  const [sessions, setSessions] = useState<UserSession[] | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  async function load() {
    try {
      const { data } = await getSessions();
      setSessions(data);
    } catch {
      toast.error("Failed to load sessions.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleRevoke(id: string) {
    setRevoking(id);
    try {
      await revokeSession(id);
      toast.success("Session revoked.");
      await load();
    } catch {
      toast.error("Failed to revoke session.");
    } finally {
      setRevoking(null);
    }
  }

  async function handleRevokeAll() {
    setRevokingAll(true);
    try {
      await revokeAllOtherSessions();
      toast.success("All other sessions revoked.");
      await load();
    } catch {
      toast.error("Failed to revoke sessions.");
    } finally {
      setRevokingAll(false);
    }
  }

  const others = sessions?.filter((s) => !s.isCurrent) ?? [];

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">Active Sessions</h2>
        {others.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            disabled={revokingAll}
            onClick={() => void handleRevokeAll()}
          >
            Revoke all others
          </Button>
        )}
      </div>

      {sessions === null ? (
        <div className="space-y-2 max-w-lg">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-muted-foreground text-sm">No active sessions.</p>
      ) : (
        <div className="space-y-2 max-w-lg">
          {sessions.map((s) => (
            <Card key={s.id} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {parseUserAgent(s.userAgent)}
                    </span>
                    {s.isCurrent && (
                      <Badge variant="secondary" className="text-xs">
                        This session
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Started {formatDate(s.createdAt)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Last active {formatDate(s.lastActiveAt)}
                  </div>
                </div>
                {!s.isCurrent && (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={revoking === s.id}
                    onClick={() => void handleRevoke(s.id)}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Access log section ─────────────────────────────────────────────────────

function AccessLogSection() {
  const [logs, setLogs] = useState<AccountLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  async function load(cursor?: string) {
    cursor ? setLoadingMore(true) : setLoading(true);
    try {
      const res = await getAccountLogs(cursor);
      setLogs((prev) => (cursor ? [...prev, ...res.data] : res.data));
      setNextCursor(res.nextCursor);
    } catch {
      toast.error("Failed to load access log.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">My Access Log</h2>

      {loading ? (
        <div className="space-y-2 max-w-lg">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <p className="text-muted-foreground text-sm">No events recorded yet.</p>
      ) : (
        <div className="space-y-1.5 max-w-lg">
          {logs.map((log) => (
            <Card key={log.id} className="p-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-mono truncate">
                    {log.eventType}
                  </span>
                  {log.provider && (
                    <Badge
                      variant="outline"
                      className="text-xs capitalize shrink-0"
                    >
                      {log.provider}
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDate(log.createdAt)}
                </span>
              </div>
            </Card>
          ))}

          {nextCursor && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={() => void load(nextCursor)}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── Delete account section ─────────────────────────────────────────────────

function DeleteAccountSection() {
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (confirmation !== "DELETE") return;
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success("Your account has been deleted.");
      await signOut();
    } catch {
      toast.error("Failed to delete account.");
      setDeleting(false);
    }
  }

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-destructive">Delete Account</h2>
      <p className="text-sm text-muted-foreground max-w-prose">
        Deleting your account is immediate and cannot be undone. Your profile
        information will be anonymised, all sessions will be revoked, and app
        access will be removed. Your activity in audit logs is preserved without
        personal identifiers.
      </p>
      <Button
        variant="destructive"
        onClick={() => setOpen(true)}
        className="min-h-[44px]"
      >
        Delete my account
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This action is immediate and irreversible. Type{" "}
            <strong className="text-foreground">DELETE</strong> to confirm.
          </p>
          <Input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setConfirmation("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirmation !== "DELETE" || deleting}
              onClick={() => void handleDelete()}
            >
              {deleting ? "Deleting…" : "Delete account"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ── Account page ───────────────────────────────────────────────────────────

export function AccountPage() {
  const { auth } = useAuth();

  if (auth.status === "loading") {
    return (
      <div className="space-y-4 max-w-2xl">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (auth.status !== "active") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-2xl space-y-10">
      <h1 className="text-2xl font-bold tracking-tight">Account</h1>

      <ProfileSection />
      <Separator />
      <PreferencesSection />
      <Separator />
      <ConnectedAccountsSection />
      <Separator />
      <SessionsSection />
      <Separator />
      <AccessLogSection />
      <Separator />
      <DeleteAccountSection />
    </div>
  );
}
